<script setup>
import { ref, useTemplateRef, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { __, __x, __n, __nx } from 'JIX/gettext';
import DsSmallTip from 'JIX/components/ds/DsSmallTip.vue';
import { isMobile } from 'JIX/utils';
import DsCheckbox from '../ds/DsCheckbox.vue';

const props = defineProps({
    categorySuggestions: {
        type: Object,
        required: true,
    },
});

const emit = defineEmits(['close']);

const store = useStore();

const categoriesToAdd = ref(props.categorySuggestions.suggestions.map(({ params }) => params));

const categorysuggestions = useTemplateRef('beforeyoucontinuebox');

const options = {
    root: null,
    threshold: 0.95,
};
const callback = (entries, _observer) => {
    entries.forEach((entry) => {
        if (entry.isIntersecting) {
            entry.target.classList.remove('opacity-0');
            if (window.jixAnalytics) {
                window.jixAnalytics('event', { category: 'new-jobsearch-before-you-continue', action: 'into-view', label: 'shown' });
            }
        }
    });
};
const observer = new IntersectionObserver(callback, options);

onMounted(() => {
    observer.observe(categorysuggestions.value.$el);
});


onBeforeUnmount(() => {
    observer.unobserve(categorysuggestions.value.$el);
});


function mergeCategoryLinks() {
    if (!categoriesToAdd.value.length) {
        return null;
    }
    const [firstCategory, ...otherCategories] = categoriesToAdd.value;
    otherCategories.forEach(category => {
        firstCategory.subid.push(category.subid[0]);
    });
    return firstCategory;
}

function handleSelectCategorySuggestion() {
    const params = mergeCategoryLinks();
    if (params) {
        store.commit('setSearchQuery', params);
        store.dispatch('performSearch');
    }
    if (window.jixAnalytics) {
        window.jixAnalytics('event', { category: 'new-jobsearch-before-you-continue', action: 'click', label: 'add_category' });
    }
    if (window.scrollTo) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    emit('close');
}

function handleDismiss() {
    if (window.jixAnalytics) {
        window.jixAnalytics('event', { category: 'new-jobsearch-before-you-continue', action: 'click', label: 'dismiss' });
    }
    emit('close');
}

</script>

<template>
    <DsSmallTip
        ref="beforeyoucontinuebox"
        class="beforeyoucontinue opacity-0"
        :heading-tag="__('Anbefaling')"
        :heading="__('Før du fortsætter ...')"
        dismissable
        @close="handleDismiss"
    >
        <div v-if="categorySuggestions.suggestions.length > 1">
            <div class="mb-1">
                {{ __('Vi anbefaler, at du tilføjer kategorier:') }}
            </div>
            <ul class="list-unstyled mb-3">
                <li v-for="category, index in categorySuggestions.suggestions" :key="category.name + index">
                    <DsCheckbox :name="category.name" :value="category.params" v-model="categoriesToAdd">
                        {{ category.name }}
                    </DsCheckbox>
                </li>
            </ul>
        </div>
        <div class="mb-3" v-else>
            {{ __x("Vi anbefaler, at du tilføjer kategorien '{category}' - så får du en bedre søgning.", { category: categorySuggestions.suggestions[0].name }) }}
        </div>
        <div class="d-flex flex-column flex-sm-row gap-2">
            <button
                type="button"
                class="btn order-1 order-sm-0"
                :class="{ 'btn-secondary': !isMobile, 'text-xs': isMobile }"
                @click="handleDismiss"
            >
                {{ __('Fortsæt uden kategori') }}
            </button>
            <button
                type="button"
                class="btn btn-primary order-0 order-sm-1"
                @click="handleSelectCategorySuggestion"
            >
                {{ __('Tilføj kategori') }}
            </button>
        </div>
    </DsSmallTip>
</template>

<style>
.opacity-0 {
    opacity: 0;
}

.beforeyoucontinue {
    transition: opacity 0.5s ease-in;
}
</style>